import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Statistic, message, Spin, Alert } from 'antd';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { 
  DownloadOutlined, 
  UserOutlined, 
  CalendarOutlined, 
  HeartOutlined,
  TrophyOutlined
} from '@ant-design/icons';
// AdminLayout now provided at router level
import AnalyticsService from '../../services/AnalyticsService';
import EventService from '../../services/EventService';
import AdvancedAnalytics from './AdvancedAnalytics';
import AnalyticsInsights from './AnalyticsInsights';
import RealtimeDashboard from '../../components/RealtimeDashboard';
import { useRealtimeAnalytics } from '../../hooks/useRealtimeAnalytics';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  ActionButton,
  StatsContainer,
  StatCard,
  ChartContainer,
  FilterContainer,
  TableContainer,
  StyledSelect,
  StyledRangePicker
} from './styled';

const { Option } = StyledSelect;

const ReportsAnalytics = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedDates, setSelectedDates] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  
  // Use realtime analytics hook
  const {
    data: analyticsData,
    loading,
    error
  } = useRealtimeAnalytics({
    timeRange,
    startDate: selectedDates?.[0]?.format('YYYY-MM-DD'),
    endDate: selectedDates?.[1]?.format('YYYY-MM-DD'),
    eventId: selectedEvent
  });
  
  // Animation state management
  const [showContent, setShowContent] = useState({
    header: false,
    filters: false,
    realtime: false,
    stats: false,
    charts: false
  });
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { setPageHeader } = usePageHeader();

  // Load data on component mount
  useEffect(() => {
    const initializePage = async () => {
      try {
        await loadEvents();
        triggerAnimations();
        setInitialLoadComplete(true);
      } catch (err) {
        console.error('Error loading events:', err);
      }
    };
    initializePage();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await EventService.getAllEvents();
      setEvents(response.data || []);
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const triggerAnimations = () => {
    setShowContent({
      header: false,
      filters: false,
      realtime: false,
      stats: false,
      charts: false
    });
    
    setTimeout(() => {
      setShowContent(prev => ({ ...prev, header: true }));
    }, 50);
    
    setTimeout(() => {
      setShowContent(prev => ({ ...prev, filters: true }));
    }, 150);

    setTimeout(() => {
      setShowContent(prev => ({ ...prev, realtime: true }));
    }, 200);
    
    setTimeout(() => {
      setShowContent(prev => ({ ...prev, stats: true }));
    }, 250);
    
    setTimeout(() => {
      setShowContent(prev => ({ ...prev, charts: true }));
    }, 350);
  };

  const handleExportReport = async () => {
    try {
      const filters = {
        timeRange,
        startDate: selectedDates?.[0]?.format('YYYY-MM-DD'),
        endDate: selectedDates?.[1]?.format('YYYY-MM-DD'),
        eventId: selectedEvent
      };
      
      const detailedData = await AnalyticsService.getDetailedScreeningData(filters);
      
      const data = {
        summary: analyticsData?.overview,
        detailed: detailedData,
        filters,
        timestamp: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bha-analytics-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      message.error('Failed to export report');
    }
  };

  // Set page header with actions (after handleExportReport is defined)
  useEffect(() => {
    setPageHeader({
      title: 'Analytics',
      actions: (
        <ActionButton 
          type="primary" 
          icon={<DownloadOutlined />}
          onClick={handleExportReport}
          loading={loading}
        >
          Export Report
        </ActionButton>
      )
    });
  }, [setPageHeader, loading]);

  const volunteerColumns = [
    {
      title: 'Rank',
      key: 'rank',
      render: (_, __, index) => (
        <span style={{ fontWeight: 'bold' }}>#{index + 1}</span>
      ),
      width: 60,
    },
    {
      title: 'Volunteer Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Screenings',
      dataIndex: 'screenings',
      key: 'screenings',
      sorter: (a, b) => a.screenings - b.screenings,
      render: (value) => <span style={{ fontWeight: 'bold' }}>{value}</span>
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      sorter: (a, b) => a.hours - b.hours,
      render: (value) => <span>{value}h</span>
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (value) => (
        <span style={{ color: value >= 4.5 ? '#52c41a' : value >= 4.0 ? '#faad14' : '#ff4d4f' }}>
          ⭐ {value}
        </span>
      )
    },
    {
      title: 'Specialties',
      dataIndex: 'specialties',
      key: 'specialties',
      render: (specialties) => (
        <div>
          {specialties?.slice(0, 2).map((specialty, index) => (
            <span key={index} style={{ 
              fontSize: '11px', 
              background: '#f0f0f0', 
              padding: '2px 6px', 
              borderRadius: '4px',
              marginRight: '4px'
            }}>
              {specialty}
            </span>
          ))}
        </div>
      )
    }
  ];

  if (loading) {
    return (
        <PageContainer style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Spin size="large" />
        </PageContainer>
    );
  }

  if (error) {
    return (
        <PageContainer>
          <Alert 
            message="Error Loading Analytics" 
            description={error}
            type="error" 
            showIcon 
            style={{ marginBottom: 24 }}
          />
        </PageContainer>
    );
  }

  return (
      <PageContainer style={{ 
        minHeight: '100vh',
        opacity: initialLoadComplete ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out'
      }}>
        <FilterContainer className={showContent.filters ? 'filters-animate-in' : 'filters-animate-out'}>
          <Row gutter={16} align="middle">
            <Col>
              <span style={{ marginRight: 8 }}>Time Range:</span>
              <StyledSelect
                value={timeRange}
                onChange={setTimeRange}
                style={{ width: 120 }}
              >
                <Option value="week">Week</Option>
                <Option value="month">Month</Option>
                <Option value="quarter">Quarter</Option>
                <Option value="year">Year</Option>
              </StyledSelect>
            </Col>
            <Col>
              <span style={{ marginRight: 8 }}>Event:</span>
              <StyledSelect
                value={selectedEvent}
                onChange={setSelectedEvent}
                style={{ width: 200 }}
                placeholder="All Events"
                allowClear
              >
                {events.map(event => (
                  <Option key={event._id} value={event._id}>
                    {event.title}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
            <Col>
              <span style={{ marginRight: 8 }}>Custom Range:</span>
              <StyledRangePicker 
                value={selectedDates}
                onChange={setSelectedDates}
              />
            </Col>
          </Row>
        </FilterContainer>

        {/* Real-time Dashboard */}
        <div className={showContent.realtime ? 'realtime-animate-in' : 'realtime-animate-out'}>
          <RealtimeDashboard style={{ marginBottom: 24 }} />
        </div>

        <StatsContainer className={showContent.stats ? 'stats-animate-in' : 'stats-animate-out'}>
          <Row gutter={24}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard>
                <Statistic
                  title="Total Patients"
                  value={analyticsData?.overview?.totalPatients || 0}
                  prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard>
                <Statistic
                  title="Health Screenings"
                  value={analyticsData?.overview?.totalScreenings || 0}
                  prefix={<HeartOutlined style={{ color: '#d32f2f' }} />}
                  valueStyle={{ color: '#d32f2f' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard>
                <Statistic
                  title="Active Events"
                  value={analyticsData?.overview?.activeEvents || 0}
                  prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard>
                <Statistic
                  title="High Risk Detected"
                  value={analyticsData?.overview?.highRiskDetected || 0}
                  prefix={<TrophyOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </StatCard>
            </Col>
          </Row>
        </StatsContainer>

        <div className={showContent.charts ? 'charts-animate-in' : 'charts-animate-out'}>
          {/* Monthly Trends */}
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={16}>
              <ChartContainer>
                <Card title="Monthly Screening Trends" bordered={false}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData?.trendsData?.monthlyData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="screenings" fill="#d32f2f" name="Screenings" />
                      <Bar dataKey="events" fill="#52c41a" name="Events" />
                      <Bar dataKey="volunteers" fill="#1890ff" name="Volunteers" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </ChartContainer>
            </Col>
            <Col xs={24} lg={8}>
              <ChartContainer>
                <Card title="Risk Distribution" bordered={false}>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData?.overview?.riskDistribution || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name} ${percentage?.toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {(analyticsData?.overview?.riskDistribution || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </ChartContainer>
            </Col>
          </Row>

          {/* Performance & Volunteers */}
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <ChartContainer>
                <Card title="Performance Over Time" bordered={false}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData?.trendsData?.monthlyData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="screenings" 
                        stroke="#d32f2f" 
                        strokeWidth={3}
                        name="Total Screenings"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="volunteers" 
                        stroke="#1890ff" 
                        strokeWidth={3}
                        name="Active Volunteers"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </ChartContainer>
            </Col>
            <Col xs={24} lg={8}>
              <TableContainer>
                <Card title="Top Volunteers" bordered={false}>
                  <Table
                    columns={volunteerColumns}
                    dataSource={analyticsData?.volunteerData?.topVolunteers || []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </TableContainer>
            </Col>
          </Row>

          {/* Risk Factors Analysis */}
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <ChartContainer>
                <Card title="Risk Factors Analysis" bordered={false}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData?.riskAnalysis?.riskFactors || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="factor" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#ff7875" name="Patients Affected" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </ChartContainer>
            </Col>
          </Row>
        </div>

        {/* Advanced Analytics Section */}
        <div style={{ marginTop: 24 }}>
          <AdvancedAnalytics analyticsData={analyticsData} />
        </div>

        {/* Analytics Insights Section */}
        <div style={{ marginTop: 24 }}>
          <AnalyticsInsights analyticsData={analyticsData} />
        </div>
      </PageContainer>
  );
};

export default ReportsAnalytics;
